<script setup>
import Divider from '@/components/shared/page/Divider.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import Markdown from '@/components/shared/transform/Markdown.vue'
import DateTime from '@/components/shared/transform/DateTime.vue'
import ImageCard from '@/components/shared/image/ImageCard.vue'
import Heading from '@/components/shared/page/Heading.vue'
import Debug from '@/components/shared/dev/Debug.vue'
import {Message} from '@/models/message.model.js'
import {Channel} from '@/models/channel.model.js'
import {dateFormats} from '@/constants/date-and-time.js'
import {useSimulatedTyping} from '@/composables/use-simulated-typing.js'
import {useScrollIntoView} from '@/composables/use-scroll-into-view.js'
import {useDebugMode} from '@/composables/use-debug-mode.js'
import {useIntersectionObserver} from '@vueuse/core'
import {computed, watch, watchEffect, toRefs, onMounted, useTemplateRef} from 'vue'

//Emits and props
const emit = defineEmits([
  'seen',
  'scroll-to-bottom',
])
const props = defineProps({
  message: {
    type: Message,
    required: true,
  },
  channel: {
    type: Channel,
    default: null,
  },
  showNew: {
    type: Boolean,
    default: false,
  },
  showTimestamps: {
    type: Boolean,
    default: false,
  },
  showCourseNames: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  shouldScrollIntoView: {
    type: Boolean,
    default: false,
  },
})

//Get refs
const {message, shouldScrollIntoView} = toRefs(props)
const isReply = computed(() => message.value.isReply)
const text = computed(() => {
  if (!message.value?.body) {
    return ''
  }
  //NOTE: For some reason some newlines coming from the server are double escaped
  return String(message.value.body).replace(/\\n/g, '\n')
})

//Scroll into view and debug mode composables
const {scrollIntoView} = useScrollIntoView()
const {isDebugMode, debugLog} = useDebugMode()

//Track visibility of message container
//We'll consider a message that is 90% in view as having been "seen" by the user
const messageContainer = useTemplateRef('messageContainer')
const {stop: stopIntersectionObserver} = useIntersectionObserver(messageContainer, ([entry]) => {
  const isVisible = (entry && entry.isIntersecting)
  if (isVisible) {
    emit('seen')
    stopIntersectionObserver()
  }
}, {threshold: 0.1})

//Scroll target includes divider + course heading above the bubble (see template)
const messageScrollRoot = useTemplateRef('messageScrollRoot')

//Simulate typing for replies
const {startTyping, typedOutput} = useSimulatedTyping(text)

//Split text into lines of text for newlines handling
const paragraphs = computed(() => (isReply.value ? typedOutput : text).value.split('\n\n'))

//Mount logic
onMounted(() => {
  if (isReply.value) {
    startTyping()
  }
})

//Scroll into view delayed
const scrollIntoViewDelayed = reason => setTimeout(() => {
  debugLog(`Scrolling message into view (${reason}):`, message.value.id)
  scrollIntoView(messageScrollRoot, {
    behavior: 'instant',
    block: 'start',
  })
}, 50)

//Scroll into view if needed (cancel pending scroll when eligibility changes so a
//stale timer cannot fire after we switch scroll targets, e.g. deep link vs last seen)
watchEffect(onCleanup => {
  if (shouldScrollIntoView.value) {
    const timeout = scrollIntoViewDelayed('initial load')
    onCleanup(() => clearTimeout(timeout))
  }
})

//On image loaded, scroll into view again if needed
const onImageLoaded = () => {
  if (shouldScrollIntoView.value) {
    scrollIntoViewDelayed('image loaded')
  }
}

//Scroll to bottom whenever typed output changes, to ensure the newly typed message remains in view
watch(typedOutput, () => emit('scroll-to-bottom'))
</script>

<template>
  <div
    ref="messageScrollRoot"
    :class="$style.wrapper"
    :data-message-id="message.id"
  >
    <Divider v-if="(showTimestamps && message.showDate) || (showCourseNames && message.showCourseName)">
      <DateTime
        v-if="message.sentAt"
        :date="message.sentAt"
        :format="dateFormats.DATE"
      />
    </Divider>
    <Heading
      v-if="showCourseNames && message.showCourseName"
      :text="message.courseName"
      :class="$style.courseName"
    />
    <div
      ref="messageContainer"
      :class="$style.container"
    >
      <div
        v-if="showTimestamps && message.showTime"
        :class="{
          [$style.timestamp]: true,
          [$style.incoming]: message.isIncoming,
        }"
      >
        <small>
          <DateTime
            :date="message.sentAt"
            :format="dateFormats.TIME"
          />
        </small>
      </div>
      <div
        v-if="message.hasEmbeddableMedia"
        :class="{
          [$style.message]: true,
          [$style.media]: true,
        }"
        test-id="chatMessageMedia"
      >
        <div
          :class="{
            [$style.content]: true,
            [$style.media]: true,
            [$style.incoming]: message.isIncoming,
            [$style.video]: message.hasUploadedVideo || message.hasExternalVideo,
          }"
        >
          <Debug v-if="isDebugMode">
            MESSAGE ID: {{ message.id }}<br>
            DIRECTION: {{ message.direction }}<br>
            SHOULD SCROLL INTO VIEW: {{ shouldScrollIntoView }}<br>
          </Debug>
          <div
            :class="$style.embed"
            test-id="chatMessageEmbed"
          >
            <!-- Uploaded videos will have a signed URL in the mediaUrl field, and can be embedded directly -->
            <video
              v-if="message.hasUploadedVideo"
              controls
              controlslist="nodownload"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
              allow="allow-presentation"
              webkit-playsinline
              playsinline
              :aria-label="message.videoTitle"
              :poster="message.imageUrl"
            >
              <source
                :src="message.mediaUrl"
                type="video/mp4"
              >
              Your browser does not support this video.
            </video>

            <!-- External videos are stored in the mediaUrl field, and need to use provider specific embed code -->
            <iframe
              v-else-if="message.hasYouTubeVideo"
              :src="`https://www.youtube.com/embed/${message.youTubeVideoId}`"
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
            />
            <iframe
              v-else-if="message.hasVimeoVideo"
              :src="`https://player.vimeo.com/video/${message.vimeoVideoId}?title=0&byline=0&portrait=0`"
              title="Vimeo video player"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
            <iframe
              v-else-if="message.hasLoomVideo"
              :src="`https://www.loom.com/embed/${message.loomVideoId}`"
              title="Loom video player"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
            <iframe
              v-else-if="message.hasMicrosoftVideo"
              :src="message.mediaUrl"
              title="Microsoft video player"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            />
            <!-- Synthesia's player only runs from the embed endpoint, even though mediaUrl is the share link -->
            <iframe
              v-else-if="message.hasSynthesiaVideo"
              :src="`https://share.synthesia.io/embeds/videos/${message.synthesiaVideoId}`"
              title="Synthesia video player"
              frameborder="0"
              allow="encrypted-media; fullscreen"
            />

            <!-- Animated gifs and images have an imageUrl field -->
            <template v-else-if="message.hasImage || message.hasGif">
              <ImageCard
                :src="message.imageUrl"
                :download-url="message.imageDownloadUrl"
                :alt="message.imageAltText"
                can-zoom
                can-download
                @loaded="onImageLoaded()"
              />
            </template>
          </div>
        </div>
        <small
          v-if="(message.hasImage || message.hasGif) && message.imageAltText"
          :class="$style.altText"
        >
          {{ message.imageAltText }}
        </small>
      </div>
      <div
        v-if="message.body"
        :class="{
          [$style.message]: true,
          [$style.incoming]: message.isIncoming,
        }"
        test-id="chatMessageBody"
      >
        <div
          :class="{
            [$style.content]: true,
            [$style.incoming]: message.isIncoming,
            [$style.error]: hasError,
          }"
        >
          <Debug v-if="isDebugMode">
            MESSAGE ID: {{ message.id }}<br>
            DIRECTION: {{ message.direction }}<br>
            SHOULD SCROLL INTO VIEW: {{ shouldScrollIntoView }}<br>
          </Debug>
          <Markdown
            v-for="(paragraph, index) in paragraphs"
            :key="index"
            :content="paragraph"
          />
        </div>
      </div>
      <div
        v-if="hasError"
        :class="$style.errorMessage"
      >
        <Icon
          icon="error"
          is-outlined
          size="xs"
        />
        <small>
          There was an error sending this message
        </small>
      </div>
    </div>
  </div>
</template>

<style module>
.wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  /* Nudge scrollIntoView(block: start) down slightly so date line + course title sit below the container edge */
  scroll-margin-top: 1rem;
}
.container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.message {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  max-width: calc(100vw - 2rem);
  &.media {
    width: 100%;
  }
  &.incoming {
    flex-direction: row-reverse;
  }
}
.avatar {
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
}
.content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  min-height: 55px; /* Match avatar height */
  max-width: 80%;
  background: var(--bg-chat-message-outgoing);
  color: var(--color-chat-message-outgoing);
  border-radius: var(--border-radius-m);
  padding: 1rem;
  overflow-wrap: break-word;
  &.incoming {
    background: var(--bg-chat-message-incoming);
    color: var(--color-chat-message-incoming);
  }
  &.error {
    background: var(--bg-chat-message-error);
    color: var(--color-chat-message-error);
  }
  &.media {
    max-height: 400px;
    max-width: 90%;
    background: none;
    padding: 0;
    @media (min-width: 768px) {
      max-width: 55%;
    }
  }
  &.video {
    width: 100%;
  }
}
.embed {
  max-width: 100%;
  img, video, iframe {
    max-height: 400px;
    max-width: 100%;
  }
  iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
  }
}
.altText {
  display: block;
  color: var(--color-text-muted);
}
.timestamp {
  display: flex;
  gap: 0.5rem;
  font-size: var(--font-size-xxs);
  color: var(--color-text-muted);
  margin-bottom: -0.5rem;
  &.incoming {
    justify-content: flex-end;
  }
  .icon {
    display: inline-block;
    margin-left: 0.25rem;
  }
}
.errorMessage {
  color: var(--color-text-danger);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-top: -0.5rem;
}
.courseName {
  margin-top: -1rem !important; /* Keep it closer to the divider */
}
</style>
